import { Edit } from '@mui/icons-material'
import ConstructionIcon from '@mui/icons-material/Construction'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import EventIcon from '@mui/icons-material/Event'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import {
  Box,
  Button,
  ButtonGroup,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material'
import React, { useCallback, useContext, useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'

import {
  AppointmentByVehicle,
  RepairOrderByVehicle,
  VehicleDetailPanel
} from '../../components/VehicleDetails/'
import { AppRoutes } from '../../models'
import { AuthContext, VehicleDetailContext } from '../../providers'
import { useGetCustomersForVehicleQuery } from '../../redux/api'
import { describeVehicle } from '../../utils'
import { formatName } from '../../utils/formatCustomerName/formatCustomerName'
import { loggingAssert } from '../../utils/handleError/loggingAssert'
import { BrowseFluidsTaxonomyButton } from '../BrowseFluidsTaxonomy/BrowseFluidsTaxonomyButton'
import { CarFaxHistoryButton } from '../CarFaxHistory/CarFaxHistoryButton'
import { OutlinedBox } from '../SharedUI'
import { CustomersByVehicle } from './CustomersByVehicle'
import { DisabledDetailForm } from './DisabledDetailForm'
import { ManualDetailForm } from './ManualDetailForm'
import NewVehicleImageDialog from './NewVehicleImageDialog'
import { UpdateLicensePlate } from './UpdateLicensePlate'
import { UpdateVin } from './UpdateVin'
import { VehicleNotesForm } from './VehicleNotesForm'

export const VehicleDetailsContainer: React.FC = () => {
  const navigate = useNavigate()
  const [dialogProps, setDialogProps] = useState<
    { type: 'appt' | 'estimate' | 'repair'; customerId: string } | undefined
  >()
  const { currentUser, userShopId } = useContext(AuthContext)
  const {
    vehicleId,
    oldVehicleInfo,
    newVehicleInfo,
    foundVin,
    vinSearched,
    vin,
    vehicleImages
  } = useContext(VehicleDetailContext)
  const [editing, setEditing] = useState(false)
  const { data: customers } = useGetCustomersForVehicleQuery({
    authorization: loggingAssert('Token not set', currentUser!.accessToken),
    shop: loggingAssert('ShopId not set', userShopId),
    vehicle: vehicleId!
  })

  const handleCreate = useCallback(
    (type: 'appt' | 'estimate' | 'repair', customerIds: string[]) => {
      if (customerIds.length === 1) {
        if (type === 'estimate') {
          navigate(
            AppRoutes.ESTIMATES +
              '/new?customerId=' +
              customerIds[0] +
              '&vehicleId=' +
              vehicleId
          )
        } else if (type === 'repair') {
          navigate(
            AppRoutes.ESTIMATES +
              '/new?isRepairOrder=yes&&customerId=' +
              customerIds[0] +
              '&vehicleId=' +
              vehicleId
          )
        } else {
          navigate(
            generatePath(AppRoutes.MANAGE_APPOINTMENTS, { id: 'new' }) +
              '?customerId=' +
              customerIds[0] +
              '&vehicleId=' +
              vehicleId
          )
        }
        setDialogProps(undefined)
      } else {
        setDialogProps({ type, customerId: customerIds[0] })
      }
    },
    [navigate, vehicleId]
  )

  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  const VehicleImage = useCallback(() => {
    const primaryVehicleImage = vehicleImages?.find(
      (image) => image.info?.isPrimary
    )
    if (!primaryVehicleImage) {
      return (
        <>
          <NewVehicleImageDialog
            isOpen={createDialogOpen}
            setIsOpen={setCreateDialogOpen}
          />
          <IconButton onClick={() => setCreateDialogOpen(true)}>
            <DirectionsCarIcon />
          </IconButton>
        </>
      )
    }
    return (
      <Box sx={{ margin: 'auto', maxWidth: '80px', maxHeight: '50px' }}>
        <CardMedia
          sx={{ maxHeight: 50, maxWidth: 50 }}
          component="img"
          image={primaryVehicleImage.info?.vehicleImageURL}
        />
      </Box>
    )
  }, [vehicleImages, createDialogOpen])

  const mergeVehicleCallback = useCallback(
    (id: string) => {
      setEditing(false)
      navigate(
        generatePath(AppRoutes.VEHICLE_DETAILS, {
          id
        })
      )
    },
    [navigate]
  )

  return (
    <>
      {dialogProps && (
        <Dialog open={true}>
          <DialogTitle>Select a Customer</DialogTitle>
          <DialogContent>
            <Typography>
              This vehicle has multiple customers associated with it. Which
              would you like to use?
            </Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="customer-select-label">Customer</InputLabel>
              <Select
                labelId="customer-select-label"
                id="customer-select"
                value={dialogProps.customerId}
                label="Customer"
                onChange={(event) => {
                  setDialogProps({
                    ...dialogProps,
                    customerId: event.target.value
                  })
                }}>
                {(customers || []).map((customer) => {
                  return (
                    <MenuItem key={customer.id!} value={customer.id!}>
                      {formatName(customer.information)}
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogProps(undefined)}>Cancel</Button>
            <Button
              onClick={handleCreate.bind(null, dialogProps.type, [
                dialogProps.customerId
              ])}>
              Continue
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {oldVehicleInfo && newVehicleInfo && (
        <Grid container spacing={1}>
          <Grid item xs={12} lg={8}>
            <OutlinedBox
              title={
                describeVehicle(oldVehicleInfo) +
                (oldVehicleInfo.archived ? ' (Archived)' : '')
              }
              actionsLeft={
                <>
                  <BrowseFluidsTaxonomyButton
                    vehicle={{
                      id: vehicleId,
                      shop: userShopId,
                      info: oldVehicleInfo
                    }}
                  />
                  <CarFaxHistoryButton
                    vehicle={{
                      id: vehicleId,
                      shop: userShopId,
                      info: oldVehicleInfo
                    }}
                  />
                </>
              }
              actionsRight={
                customers &&
                customers[0] && (
                  <ButtonGroup
                    size="small"
                    variant="outlined"
                    aria-label="outlined button group">
                    <Button
                      startIcon={<EventIcon />}
                      aria-label="createAppointmentButton"
                      onClick={handleCreate.bind(
                        null,
                        'appt',
                        customers.map((customer) => customer.id!)
                      )}>
                      Create Appointment
                    </Button>
                    <Button
                      startIcon={<ReceiptLongIcon />}
                      onClick={handleCreate.bind(
                        null,
                        'estimate',
                        customers.map((customer) => customer.id!)
                      )}>
                      Create Estimate
                    </Button>
                    <Button
                      startIcon={<ConstructionIcon />}
                      onClick={handleCreate.bind(
                        null,
                        'repair',
                        customers.map((customer) => customer.id!)
                      )}>
                      Create Repair Order
                    </Button>
                  </ButtonGroup>
                )
              }
              logo={<VehicleImage />}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={3}>
                <Box sx={{ width: '100%' }}>
                  {!editing && <VehicleDetailPanel />}
                  {editing && (
                    <Grid container spacing={2} mb={2}>
                      <Grid item xs={6}>
                        <UpdateLicensePlate
                          mergeCallback={mergeVehicleCallback}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <UpdateVin mergeCallback={mergeVehicleCallback} />
                      </Grid>
                    </Grid>
                  )}
                  {editing && vinSearched && foundVin && (
                    <DisabledDetailForm closeForm={() => setEditing(false)} />
                  )}
                  {editing && (!vinSearched || !foundVin || !vin) && (
                    <ManualDetailForm closeForm={() => setEditing(false)} />
                  )}
                </Box>
                <Box>
                  {!editing && (
                    <IconButton
                      onClick={() => setEditing(true)}
                      aria-label="editVehicleButton">
                      <Edit />
                    </IconButton>
                  )}
                </Box>
              </Stack>
              <Divider />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={2}>
                {customers && <CustomersByVehicle customers={customers} />}
              </Stack>
              <Divider />
              <VehicleNotesForm />
              <RepairOrderByVehicle />
            </OutlinedBox>
          </Grid>
          <Grid item xs={12} lg={4}>
            <AppointmentByVehicle />
          </Grid>
        </Grid>
      )}
    </>
  )
}
